<script setup lang="ts">
import { XMarkIcon } from "@heroicons/vue/24/outline";

defineProps<{ open: boolean }>();
const emit = defineEmits<{ close: [] }>();

function onBackdrop(event: MouseEvent) {
  // Close only on a true backdrop click. The inner dialog uses
  // `@click.stop` to block bubbling for normal clicks; this extra
  // target check additionally guards against the drag case
  // (mousedown inside the dialog, mouseup on the backdrop) where
  // the resulting click event's target is the backdrop itself.
  if (event.target === event.currentTarget) emit("close");
}
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition ease-out duration-150"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition ease-in duration-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="open"
        data-testid="help-overlay"
        class="fixed inset-0 z-40 flex items-center justify-center p-4"
        style="background-color: var(--color-backdrop)"
        role="dialog"
        aria-modal="true"
        aria-labelledby="help-title"
        @click="onBackdrop"
      >
        <div
          class="relative w-full max-w-lg rounded-lg bg-bg-main shadow-xl p-6"
          @click.stop
        >
          <button
            type="button"
            data-testid="help-close"
            class="absolute right-4 top-4 rounded p-1 text-text-tertiary hover:bg-bg-hover transition-colors"
            aria-label="Close help"
            @click="emit('close')"
          >
            <XMarkIcon class="h-5 w-5" aria-hidden="true" />
          </button>
          <h2 id="help-title" class="text-lg font-semibold text-text-primary mb-3">
            About Rally Segmentation
          </h2>
          <div class="space-y-3 text-sm text-text-secondary">
            <p>
              Upload a table-tennis match video. We split it into rallies using
              ball motion and serve cues so you can review each point separately.
            </p>
            <p>
              Click <span class="font-medium text-text-primary">+ New Job</span>
              to upload an MP4. Processing takes a few minutes — the page
              auto-updates when it finishes.
            </p>
            <p class="text-xs text-text-tertiary">
              MP4 only · up to 5 GB per upload.
            </p>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
